<template>
  <v-card flat class="sub-site-wrapper">
    <div
      v-if="whatsAppConnectionsPerAgencyStats"
      class="table-statistics-wrapper"
    >
      <v-row>
        <v-col cols="12" sm="4">
          <div class="statistics-text-box elevation-3">
            <h5>Agenturen mit mind. einer WhatsApp Verbindung:</h5>
            <h3>
              {{
                whatsAppConnectionsPerAgencyStats.totalAgenciesWithConnections
              }}
            </h3>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="statistics-text-box elevation-3">
            <h5>Gesamtzahl der Verbindungen:</h5>
            <h3>{{ whatsAppConnectionsPerAgencyStats.totalConnections }}</h3>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-text-field
            class="search-field"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            label="Suche nach Agentur"
            hide-details
            single-line
            rounded="lg"
            v-model="searchQuery"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="filteredAgencies"
            :loading="loading"
            class="elevation-1"
            item-key="agencyId"
            item-value="agencyId"
            show-expand
            v-model:expanded="expanded"
          >
            <template v-slot:[`item.companyName`]="{ item }">
              <router-link :to="`/agency/${item.agencyId}`" class="agency-link">
                {{ item.companyName }}
              </router-link>
            </template>
            <template v-slot:[`item.agencyEmail`]="{ item }">
              {{ item.agencyEmail }}
            </template>
            <template v-slot:[`item.totalConnections`]="{ item }">
              {{ item.totalConnections }}
            </template>
            <template v-slot:[`item.activeConnections`]="{ item }">
              <v-chip
                :color="item.activeConnections > 0 ? 'success' : 'grey'"
                size="small"
              >
                {{ item.activeConnections }}
              </v-chip>
            </template>
            <template v-slot:[`item.connectionStatusSummary`]="{ item }">
              <div class="status-summary">
                <v-chip
                  v-if="getStatusCount(item, 'gut') > 0"
                  color="success"
                  size="small"
                  class="status-chip-inline"
                >
                  {{ getStatusCount(item, 'gut') }} gut
                </v-chip>
                <v-chip
                  v-if="getStatusCount(item, 'Fehler') > 0"
                  color="error"
                  size="small"
                  class="status-chip-inline"
                >
                  {{ getStatusCount(item, 'Fehler') }} Fehler
                </v-chip>
                <v-chip
                  v-if="getStatusCount(item, 'Abgelaufen') > 0"
                  color="warning"
                  size="small"
                  class="status-chip-inline"
                >
                  {{ getStatusCount(item, 'Abgelaufen') }} Abgelaufen
                </v-chip>
              </div>
            </template>
            <template v-slot:[`item.totalActiveAutomations`]="{ item }">
              {{ item.totalActiveAutomations }}
            </template>
            <template v-slot:[`item.totalMessages`]="{ item }">
              {{ item.totalMessages }}
            </template>
            <template v-slot:[`item.totalConversations`]="{ item }">
              {{ item.totalConversations }}
            </template>
            <template v-slot:expanded-row="{ item }">
              <tr>
                <td :colspan="headers.length" class="expanded-row-cell">
                  <div class="expanded-content">
                    <h4 class="expanded-title">
                      Verbindungsdetails ({{ item.connections.length }})
                    </h4>
                    <div class="connections-grid">
                      <div
                        v-for="conn in item.connections"
                        :key="conn._id"
                        class="connection-detail"
                      >
                        <div class="connection-header">
                          <h5>{{ conn.name || "Unbenannte Verbindung" }}</h5>
                          <v-chip
                            :color="getStatusColor(conn.status)"
                            size="small"
                            class="status-chip"
                          >
                            {{ conn.status }}
                          </v-chip>
                        </div>
                        <div class="connection-info">
                          <p><strong>Status:</strong> {{ conn.status }}</p>
                          <p>
                            <strong>Aktive Automatisierungen:</strong>
                            {{ conn.activeAutomations }}
                          </p>
                          <p>
                            <strong>Nachrichten:</strong>
                            {{ conn.messageCount }}
                          </p>
                          <p>
                            <strong>Konversationen:</strong>
                            {{ conn.conversationCount }}
                          </p>
                          <p>
                            <strong>Telefonnummern:</strong>
                            {{ conn.phoneNumbersCount }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <div class="statistics-text-box elevation-3">
            <h5>Informationen zu den Statistiken:</h5>
            <p>
              Diese Statistik zeigt alle Agenturen mit mindestens einer WhatsApp
              Verbindung.
            </p>
            <p>
              Die Statistiken wurden am <b>{{ statisticsAge }}</b> erstellt.
            </p>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  data() {
    return {
      searchQuery: "",
      expanded: [],
      headers: [
        { title: "Firmenname", key: "companyName", sortable: true },
        { title: "E-Mail", key: "agencyEmail", sortable: true },
        {
          title: "Gesamt Verbindungen",
          key: "totalConnections",
          sortable: true,
        },
        {
          title: "Aktive Verbindungen",
          key: "activeConnections",
          sortable: true,
        },
        {
          title: "Verbindungsstatus",
          key: "connectionStatusSummary",
          sortable: false,
        },
        {
          title: "Aktive Automatisierungen",
          key: "totalActiveAutomations",
          sortable: true,
        },
        { title: "Nachrichten", key: "totalMessages", sortable: true },
        { title: "Konversationen", key: "totalConversations", sortable: true },
      ],
    };
  },
  created() {
    this.loadStatistics();
  },
  computed: {
    ...mapState("statistics", [
      "whatsAppConnectionsPerAgencyStats",
      "loading",
      "statisticsAge",
    ]),
    filteredAgencies() {
      if (
        !this.whatsAppConnectionsPerAgencyStats ||
        !this.whatsAppConnectionsPerAgencyStats.agencies
      ) {
        return [];
      }
      if (!this.searchQuery) {
        return this.whatsAppConnectionsPerAgencyStats.agencies;
      }
      const query = this.searchQuery.toLowerCase();
      return this.whatsAppConnectionsPerAgencyStats.agencies.filter(
        (agency) =>
          (agency.companyName &&
            agency.companyName.toLowerCase().includes(query)) ||
          (agency.agencyEmail &&
            agency.agencyEmail.toLowerCase().includes(query))
      );
    },
  },
  methods: {
    ...mapActions("statistics", ["fetchMostRecentStatistic"]),
    async loadStatistics() {
      await this.fetchMostRecentStatistic();
    },
    getStatusColor(status) {
      switch (status) {
        case "gut":
          return "success";
        case "Fehler":
          return "error";
        case "Abgelaufen":
          return "warning";
        default:
          return "grey";
      }
    },
    getStatusCount(item, status) {
      if (!item.connections || !Array.isArray(item.connections)) {
        return 0;
      }
      return item.connections.filter((conn) => conn.status === status).length;
    },
  },
};
</script>

<style lang="scss" scoped>
.table-statistics-wrapper {
  padding: 24px;
}

.statistics-text-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  font-weight: 500;
  border: 1px solid rgb(196, 196, 196);
  border-radius: 12px;
  padding: 12px;
}

.statistics-text-box p {
  font-weight: 400;
  font-size: 14px;
}

.search-field {
  margin-bottom: 16px;
}

.agency-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.expanded-row-cell {
  padding: 24px !important;
  background-color: #f9fafb;
}

.expanded-content {
  width: 100%;
}

.expanded-title {
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.connections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.connection-detail {
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.connection-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;

  h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    flex: 1;
  }
}

.status-chip {
  margin-left: 8px;
}

.connection-info {
  p {
    margin: 6px 0;
    font-size: 0.875rem;
    color: #666;
  }
}

.status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.status-chip-inline {
  margin: 2px;
}
</style>
